import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createI18n } from 'vue-i18n';
import App from './App.vue';
import router from './router';

// Tailwind CSS
import './assets/styles/main.css';

// FontAwesome
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faBolt,
  faShieldAlt,
  faHandshake,
  faMicrochip,
  faMotorcycle,
  faBox,
  faTruck,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faClock,
  faBars,
  faTimes,
  faArrowRight,
  faCheckCircle,
  faCheck,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';

import {
  faWhatsapp,
  faInstagram,
  faFacebookF,
  faLinkedinIn,
} from '@fortawesome/free-brands-svg-icons';

// Agregar iconos a la biblioteca
library.add(
  faBolt,
  faShieldAlt,
  faHandshake,
  faMicrochip,
  faMotorcycle,
  faBox,
  faTruck,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faClock,
  faBars,
  faTimes,
  faArrowRight,
  faCheckCircle,
  faCheck,
  faInfoCircle,
  faWhatsapp,
  faInstagram,
  faFacebookF,
  faLinkedinIn
);

// i18n messages (simple por ahora, expandir después)
const messages = {
  es: {
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      about: 'Nosotros',
      contact: 'Contáctanos',
      tracking: 'Sigue tu Pedido',
      news: 'Noticias',
    },
    hero: {
      title: '¿Qué quieres enviar?',
      subtitle: 'Servicios de mensajería express en Santiago',
      cta: {
        login: 'Ingreso Clientes',
        service: 'Toma Servicio',
        track: 'Estado de tu Servicio',
        assistant: 'Asistente en Línea',
      },
    },
    services: {
      title: 'Nuestros Servicios',
      express: {
        title: 'Servicio Express',
        description: 'Nuestro servicio de mensajería express es una solución rápida, eficiente y confiable para que tus envíos y gestiones se realicen en el menor tiempo posible.',
        cta: 'Solicitar Servicio Express',
      },
      junior: {
        title: 'Junior Corporativo',
        description: 'Ofrecemos un servicio de junior motorizado exclusivo para empresas, diseñado para satisfacer todas las necesidades de mensajería interna y externa.',
        cta: 'Solicitar Junior Corporativo',
      },
      delivery: {
        title: 'Delivery Programado',
        description: 'Nuestro servicio Delivery consiste en el retiro, transporte y distribución de documentos / encomiendas, en tiempo planificado y con confirmación de entrega.',
        cta: 'Solicitar Delivery Programado',
      },
    },
    values: {
      title: 'Nuestros Valores',
      speed: {
        title: 'Rapidez',
        description: 'La rapidez es fundamental en nuestro servicio. Nos comprometemos a realizar tus entregas de manera ágil y en el menor tiempo posible dentro de la Región Metropolitana.',
      },
      trust: {
        title: 'Confianza',
        description: 'Con más de 25 años en el mercado, la confianza es uno de nuestros pilares fundamentales. Nuestros clientes saben que pueden contar con un servicio de alta calidad.',
      },
      security: {
        title: 'Seguridad',
        description: 'Trabajamos día a día para que tus envíos lleguen en perfectas condiciones y a tiempo. Ofrecemos un servicio seguro, garantizando la ejecución correcta de tus envíos.',
      },
      technology: {
        title: 'Tecnología',
        description: 'La tecnología es clave en nuestro servicio. Nuestro software propio optimiza las entregas y permite el seguimiento en tiempo real.',
      },
    },
    contact: {
      title: 'Contáctanos',
      subtitle: 'Si tienes alguna duda o consulta completa el formulario y te contactaremos lo antes posible',
      form: {
        name: 'Nombre',
        email: 'Dirección de correo electrónico',
        phone: 'Teléfono',
        message: 'Mensaje',
        submit: 'Enviar',
      },
    },
    news: {
      title: 'Noticias',
      subtitle: 'Síguenos en Instagram para más novedades',
    },
    footer: {
      rights: 'Motoboy © 2025',
      address: 'Barcelona 2077 Piso 6, Providencia',
      schedule: 'Horario de atención: Lunes a Viernes de 08:00 a 18:00 hrs (excepto festivos)',
      workWithUs: 'Trabaja con nosotros',
      commercial: 'Área Comercial',
      policies: 'Políticas de Tarifas y Seguro Motoboy',
    },
    tracking: {
      title: 'Seguimiento de Pedido',
      subtitle: 'Ingresa tu número de seguimiento para ver el estado de tu envío en tiempo real',
      form: {
        label: 'Número de seguimiento',
        placeholder: 'Ej: FALL-SAG19D13',
        button: 'Buscar',
        loading: 'Buscando...',
      },
      map: {
        location: 'Ubicación actual',
        eta: 'Tiempo estimado',
        status: 'Estado',
        waiting: 'Esperando ubicación del conductor',
      },
      details: {
        title: 'Detalles del Envío',
        origin: 'Origen',
        destination: 'Destino',
        service: 'Tipo de servicio',
        tracking: 'N° Seguimiento',
      },
      timeline: {
        title: 'Estado del Pedido',
      },
      contact: {
        driver: 'Contactar Conductor',
      },
      empty: 'Ingresa un número de seguimiento para comenzar',
      not_found: {
        title: 'Pedido no encontrado',
        description: 'Verifica que el código de seguimiento sea correcto e intenta nuevamente.',
      },
      auto_refresh: 'Actualización automática cada 30s',
    },
  },
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      about: 'About Us',
      contact: 'Contact',
      tracking: 'Track Your Order',
      news: 'News',
    },
    hero: {
      title: 'What do you want to send?',
      subtitle: 'Express courier services in Santiago',
      cta: {
        login: 'Client Login',
        service: 'Request Service',
        track: 'Track Your Service',
        assistant: 'Online Assistant',
      },
    },
    tracking: {
      title: 'Order Tracking',
      subtitle: 'Enter your tracking number to see your shipment status in real time',
      form: {
        label: 'Tracking number',
        placeholder: 'Ex: FALL-SAG19D13',
        button: 'Search',
        loading: 'Searching...',
      },
      map: {
        location: 'Current location',
        eta: 'Estimated time',
        status: 'Status',
        waiting: 'Waiting for driver location',
      },
      details: {
        title: 'Shipment Details',
        origin: 'Origin',
        destination: 'Destination',
        service: 'Service type',
        tracking: 'Tracking #',
      },
      timeline: {
        title: 'Order Status',
      },
      contact: {
        driver: 'Contact Driver',
      },
      empty: 'Enter a tracking number to get started',
      not_found: {
        title: 'Order not found',
        description: 'Check that the tracking code is correct and try again.',
      },
      auto_refresh: 'Auto-refresh every 30s',
    },
    // ... (agregar traducciones completas después)
  },
};

const i18n = createI18n({
  legacy: false,
  locale: 'es',
  fallbackLocale: 'en',
  messages,
});

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(router);
app.use(i18n);
app.component('font-awesome-icon', FontAwesomeIcon);

app.mount('#app');
